<template>
  <div class="page">
    <!-- HEADER -->
    <div class="page-head">
      <div>
        <h1>App Settings</h1>
      </div>
    </div>

    <!-- FORM -->
    <div class="card form-card">
      <div class="form-row">
        <!-- Withdrawal Commission -->
        <div class="field">
          <label>Withdrawal Commission (%)</label>
          <div class="input">
            <i class="bx bx-transfer"></i>
            <input
              type="number"
              step="0.01"
              min="0"
              v-model.number="form.withdrawalCommission"
              placeholder="2"
            />
          </div>
        </div>

        <!-- Deposit Charge -->
        <div class="field">
          <label>Deposit Charge (Fixed)</label>
          <div class="input">
            <i class="bx bx-rupee"></i>
            <input
              type="number"
              step="1"
              min="0"
              v-model.number="form.depositCharge"
              placeholder="10"
            />
          </div>
        </div>

        <!-- Credit Interest Rate -->
        <div class="field">
          <label>Credit Interest Rate (%)</label>
          <div class="input">
            <i class="bx bx-trending-up"></i>
            <input
              type="number"
              step="0.01"
              min="0"
              v-model.number="form.creditInterestRate"
              placeholder="2.5"
            />
          </div>
        </div>
      </div>

      <!-- ✅ SAVE BUTTON BELOW FORM -->
      <div class="save-row">
        <button class="btn primary" :disabled="saving" @click="saveNow">
          <i v-if="!saving" class="bx bx-save"></i>
          <span v-else class="loader"></span>
          {{ saving ? "Saving..." : "Save Settings" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from "vue";
import { useToast } from "vue-toastification";

import { getAppSettings, saveAppSettings } from "@/apis/appcon";

const toast = useToast();

const saving = ref(false);
const loading = ref(false);

/* admin */
const ldata = ref(null);
const admin_id = ref(""); // should be user_id (not email)

/* defaults */
const defaults = () => ({
  withdrawalCommission: 2,
  depositCharge: 10,
  creditInterestRate: 2.5,

  // optional gateway
  pgKey: "",
  pgSecret: "",
});

const form = reactive(defaults());
const snapshot = ref(JSON.stringify(form));

const applyFromApi = (s) => {
  form.withdrawalCommission = Number(s?.withdrawal_commission ?? 0);
  form.depositCharge = Number(s?.deposit_charge ?? 0);
  form.creditInterestRate = Number(s?.credit_interest_rate ?? 0);

  // optional
  form.pgKey = String(s?.pg_key ?? "");
  form.pgSecret = String(s?.pg_secret ?? "");

  snapshot.value = JSON.stringify(form);
};

const loadSettings = async () => {
  try {
    loading.value = true;

    const res = await getAppSettings(admin_id.value);
    if (!res || !res.status) {
      toast.error(res?.success || "Failed to load settings");
      return;
    }

    applyFromApi(res.settings);
  } catch (e) {
    toast.error("Request Failed");
  } finally {
    loading.value = false;
  }
};

const saveNow = async () => {
  try {
    saving.value = true;

    // basic validation
    if (
      Number(form.withdrawalCommission) < 0 ||
      Number(form.depositCharge) < 0 ||
      Number(form.creditInterestRate) < 0
    ) {
      toast.error("Values cannot be negative");
      return;
    }

    const res = await saveAppSettings({
      admin_id: admin_id.value,

      withdrawal_commission: String(form.withdrawalCommission ?? 0),
      deposit_charge: String(form.depositCharge ?? 0),
      credit_interest_rate: String(form.creditInterestRate ?? 0),

      // optional
      pg_key: String(form.pgKey ?? ""),
      pg_secret: String(form.pgSecret ?? ""),
    });

    if (!res || !res.status) {
      toast.error(res?.success || "Save failed");
      return;
    }

    snapshot.value = JSON.stringify(form);
    toast.success("Settings saved ✅");
  } catch (e) {
    toast.error("Save failed");
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  try {
    ldata.value = JSON.parse(sessionStorage.getItem("ldata") || "{}");

    // ✅ FIX: admin_id should come from logged-in user_id
    admin_id.value =
      ldata.value?.user_id ||
      "1";

    await loadSettings();
  } catch (e) {
    // ignore
  }
});
</script>



<style scoped>
.page {
  padding: 18px 20px 30px;
}

/* header */
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.page-head h1 {
  font-size: 22px;
  font-weight: 900;
  letter-spacing: -0.3px;
  margin: 0;
}
.page-head p {
  margin: 6px 0 0;
  font-size: 13px;
  font-weight: 650;
  color: var(--muted);
}

/* card */
.card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 16px;
  box-shadow: var(--shadow-sm);
}

/* ✅ full width card */
.form-card {
  width: 100%;
}

/* ✅ horizontal fields */
.form-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.field label {
  font-size: 12px;
  font-weight: 800;
  color: rgba(15, 23, 42, 0.82);
  margin-bottom: 7px;
  display: block;
}

.input {
  position: relative;
}

.input i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.input input {
  width: 100%;
  height: 48px;
  padding: 0 14px 0 44px;
  border: 1px solid var(--line);
  border-radius: 18px;
  outline: none;
  background: rgba(255, 255, 255, 0.96);
  font-size: 13px;
  font-weight: 650;
}
.input input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

/* ✅ save button row below */
.save-row {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

/* button */
.btn {
  cursor: pointer;
  border: none;
  border-radius: 16px;
  padding: 12px 18px;
  font-weight: 900;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
}
.btn i {
  font-size: 18px;
}
.btn.primary {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 18px 24px rgba(46, 109, 231, 0.22);
}
.btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

/* loader */
.loader {
  height: 16px;
  width: 16px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.55);
  border-top-color: #fff;
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ✅ responsive */
@media (max-width: 980px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  .save-row {
    justify-content: stretch;
  }
  .btn.primary {
    width: 100%;
  }
}
</style>
